import { useFormContext, Controller, type FieldValues, type Path, type UseFormSetValue } from 'react-hook-form';
import type { roundedType } from 'pink-lava-ui/types';
import { Space, Input } from 'antd';

export type CombinedTextField<T> = {
  name: keyof T & string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  validate?: (_value: any) => boolean | string;
  maxLength?: number;
  width?: string | number;
  onChange?: (_value: string, _setValue: UseFormSetValue<FieldValues>) => void;
};

export const CombinedTextInput = <T extends FieldValues = any>({
  fields,
  label,
  required,
  size = 'middle',
  className = '',
  rounded,
  showErrorMessage,
  disabled,
  info,
}: {
  name: string | Path<T>;
  fields: CombinedTextField<T>[];
  label?: string;
  disabled?: boolean;
  required?: boolean;
  showErrorMessage?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  rounded?: roundedType;
  info?: string;
}) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  // Determine input class based on rounded prop
  const inputClassName = `${rounded === 'large' ? '!rounded-full' : rounded ? '!rounded-lg' : ''} ${className}`;

  return (
    <div className="w-full">
      {label && (
        <label className="block font-semibold text-black mb-1 font-nunito">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Space.Compact>
        {fields.map(item => (
          <Controller
            key={item.name}
            name={item.name as string}
            control={control}
            rules={{
              required: item.required ? 'This field is required' : false,
              validate: value => {
                if (item.validate) {
                  const customResult = item.validate(value);
                  if (customResult !== true) return customResult;
                }
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                placeholder={item.placeholder}
                disabled={disabled || item.disabled}
                required={item.required}
                type={'text'}
                maxLength={item.maxLength}
                size={size}
                className={`${inputClassName} ${error ? 'border-red-500!' : ''}`}
                onChange={e => {
                  if (item.onChange) item.onChange?.(e.target.value, setValue);
                  else field.onChange(e);
                }}
                style={{ width: item.width }}
              />
            )}
          />
        ))}
      </Space.Compact>
      {info && (
        <div className="mt-1">
          <p className="text-gray-500 text-xs">{info}</p>
        </div>
      )}
      {showErrorMessage &&
        fields.find(field => {
          const fieldError = errors[field.name as string];
          return fieldError?.message;
        })?.name && (
          <div className="mt-1">
            <p className="text-red-500 text-xs">Nomor polisi kendaraan tidak valid</p>
          </div>
        )}
    </div>
  );
};
